// Cube state validation and invariants.
//
// All states Rubis produces come from legal moves, so they are always
// solvable. These checks exist to (a) prove that property in tests and
// (b) detect impossible states in diagnostics. Scope is documented per check.

import { U, D, R, L, F, B } from "./model.js";
import { extractCubies, cubieValid, normalize3x3 } from "./cubie.js";

// NOTE: total sticker parity is NOT a validity invariant (a single quarter
// turn changes it), so it is deliberately absent. Checks here target
// invariants that legal states provably preserve.

// Center color counts for N >= 4: across all six faces, each color must
// appear exactly (N-2)^2 times in the center areas (center pieces move
// between faces, but the multiset of center stickers per color is fixed).
export function centerCountsValid(state) {
  const N = Math.sqrt(state[0].length);
  if (N < 4) return true;
  const area = N * N;
  const counts = new Array(6).fill(0);
  for (let f = 0; f < 6; f++) {
    const face = state[f];
    for (let r = 1; r <= N - 2; r++) {
      for (let c = 1; c <= N - 2; c++) {
        const id = face[r * N + c];
        counts[(id / area) | 0]++;
      }
    }
  }
  for (let col = 0; col < 6; col++) {
    if (counts[col] !== (N - 2) * (N - 2)) return false;
  }
  return true;
}

// For odd N >= 3: the six unique face-center stickers may move between faces
// only as a set (e.g. under whole-cube rotations). The center cells must hold
// exactly those six stickers.
export function fixedCentersValid(state) {
  const N = Math.sqrt(state[0].length);
  if (N % 2 === 0) return true;
  const area = N * N;
  const mid = (N - 1) / 2;
  const cell = mid * N + mid;
  const centerIds = new Set();
  for (let f = 0; f < 6; f++) centerIds.add(f * area + cell);
  const seen = new Set();
  for (let f = 0; f < 6; f++) {
    const id = state[f][cell];
    if (!centerIds.has(id)) return false;
    if (seen.has(id)) return false;
    seen.add(id);
  }
  return true;
}

// 2x2 corner orientation: sum of corner twists mod 3 must be 0.
// Each corner slot lists its three sticker cells [face*4+cell, ...] in a
// fixed order; twist = which position the U/D-colored sticker occupies.
const CORNER2_SLOTS = [
  [U * 4 + 0, L * 4 + 0, B * 4 + 2], // UBL
  [U * 4 + 1, L * 4 + 2, F * 4 + 0], // UFL
  [U * 4 + 2, R * 4 + 2, B * 4 + 0], // UBR
  [U * 4 + 3, R * 4 + 0, F * 4 + 2], // URF
  [D * 4 + 0, L * 4 + 1, B * 4 + 3], // DBL
  [D * 4 + 1, L * 4 + 3, F * 4 + 1], // DLF
  [D * 4 + 2, R * 4 + 3, B * 4 + 1], // DRB
  [D * 4 + 3, R * 4 + 1, F * 4 + 3], // DRF
];

// Canonical triples for the eight 2x2 corner positions (same convention as
// cubie.js CORNER_CANON): UBL, UFL, UBR, URF, DBL, DLF, DRB, DRF.
const CORNER2_CANON = [
  [U, B, L],
  [U, L, F],
  [U, R, B],
  [U, F, R],
  [D, L, B],
  [D, F, L],
  [D, B, R],
  [D, R, F],
];

export function cornerOrientationValid2x2(state) {
  const N = Math.sqrt(state[0].length);
  if (N !== 2) return true;
  const area = 4;
  let twist = 0;
  for (let s = 0; s < 8; s++) {
    const slot = CORNER2_SLOTS[s];
    for (let i = 0; i < 3; i++) {
      const id = state[(slot[i] / area) | 0][slot[i] % area];
      const col = (id / area) | 0;
      if (col === U || col === D) {
        const face = (slot[i] / area) | 0;
        twist += CORNER2_CANON[s].indexOf(face);
        break;
      }
    }
  }
  return twist % 3 === 0;
}

// 3x3 invariants: corner orientation, edge orientation, permutation parity.
// Uses cubie-level extraction (same machinery as the two-phase solver).
// The permutation parity check requires a canonical cube orientation, so the
// state is normalized by undoing whole-cube rotations first.
export function validate3x3(state) {
  const N = Math.sqrt(state[0].length);
  if (N !== 3) return { valid: true, reason: "only applicable to 3x3" };
  let cubies;
  try {
    cubies = extractCubies(normalize3x3(state));
  } catch (e) {
    return { valid: false, reason: `bad cubie state: ${e.message}` };
  }
  return cubieValid(cubies);
}

// General validation combining the checks above with honest scope notes.
export function validateState(state) {
  const N = Math.sqrt(state[0].length);
  if (N % 2 === 1 && !fixedCentersValid(state)) {
    return { valid: false, reason: "face centers moved" };
  }
  if (N >= 4 && !centerCountsValid(state)) {
    return { valid: false, reason: "center color counts" };
  }
  if (N === 2 && !cornerOrientationValid2x2(state)) {
    return { valid: false, reason: "corner orientation" };
  }
  if (N === 3) {
    const r = validate3x3(state);
    if (!r.valid) return r;
  }
  return { valid: true, reason: "" };
}
